import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InquiryType } from '@kontor/shared';
import { api, ApiError } from '../api';
import { useInquiryTypes } from '../inquiryTypes';
import { useSession } from '../session';

/**
 * Bereich „Anfragetypen“ der Einrichtung. Die Rechte prüft der Server, die Oberfläche blendet nur aus.
 */
export function SetupPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const types = useInquiryTypes();
  const canWrite = can('setup.write');
  const canDelete = can('setup.delete');
  const roles = useQuery({ queryKey: ['inquiry-type-roles'], queryFn: async () => (await api<{ data: { key: string; name: string }[] }>('GET', '/inquiry-types/roles')).data, enabled: canWrite });
  const roleNames = (keys: string[]) => (keys.length === 0 ? 'Alle Rollen' : keys.map((k) => roles.data?.find((r) => r.key === k)?.name ?? k).join(', '));

  const refresh = () => client.invalidateQueries({ queryKey: ['inquiry-types'] });
  const reorder = useMutation({
    mutationFn: (keys: string[]) => api<{ data: InquiryType[] }>('POST', '/inquiry-types/order', { keys }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (key: string) => api('DELETE', `/inquiry-types/${key}`),
    onSuccess: refresh,
  });

  const move = (index: number, delta: -1 | 1) => {
    const keys = types.types.map((t) => t.key);
    const target = index + delta;
    if (target < 0 || target >= keys.length) return;
    [keys[index], keys[target]] = [keys[target]!, keys[index]!];
    reorder.mutate(keys);
  };

  const error = [reorder.error, remove.error].find((e) => e instanceof ApiError) as ApiError | undefined;

  return (
    <>
      <section className="card">
        <div className="page-head">
          <h2>Anfragetypen</h2>
          {canWrite && <Link className="btn btn--primary btn--sm page-head__action" to="/einrichtung/anfragetypen/neu">Neuer Anfragetyp</Link>}
        </div>
        <p className="muted small">
          Jeder Anfragetyp bestimmt, welche Felder eine Anfrage hat, zum Beispiel Auszugsadresse und Wohnfläche beim Umzug. Die Webseite
          sendet die Felder unter ihrer Kennung.
        </p>

        {error && <div className="alert alert--error" role="alert">{error.message}</div>}

        <table className="table table--cards">
          <thead>
            <tr>
              <th>Bezeichnung</th>
              <th>Kennung</th>
              <th>Felder</th>
              <th>Sichtbar für</th>
              <th><span className="sr-only">Aktionen</span></th>
            </tr>
          </thead>
          <tbody>
            {types.isLoading && (
              <tr><td colSpan={5} className="muted">Wird geladen …</td></tr>
            )}
            {!types.isLoading && types.types.length === 0 && (
              <tr><td colSpan={5} className="empty">Noch keine Anfragetypen angelegt.</td></tr>
            )}
            {types.types.map((t, index) => (
              <tr key={t.key}>
                <td>{canWrite ? <Link to={`/einrichtung/anfragetypen/${t.key}`} className="strong-link">{t.label}</Link> : t.label}</td>
                <td data-label="Kennung" className="muted">{t.key}</td>
                <td data-label="Felder">{t.fields.length}</td>
                <td data-label="Sichtbar für">{roleNames(t.visibleRoleKeys)}</td>
                <td className="cell-actions">
                  <div className="row-actions">
                  {canWrite && (
                    <>
                      <button type="button" className="btn btn--secondary btn--sm" aria-label={`${t.label} nach oben`} disabled={index === 0 || reorder.isPending} onClick={() => move(index, -1)}>↑</button>
                      <button type="button" className="btn btn--secondary btn--sm" aria-label={`${t.label} nach unten`} disabled={index === types.types.length - 1 || reorder.isPending} onClick={() => move(index, 1)}>↓</button>
                      <Link className="btn btn--secondary btn--sm" to={`/einrichtung/anfragetypen/${t.key}`}>Bearbeiten</Link>
                    </>
                  )}
                  {canDelete && (
                    <button
                      type="button"
                      className="btn btn--danger btn--sm"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (window.confirm(`Anfragetyp „${t.label}“ wirklich löschen?`)) remove.mutate(t.key);
                      }}
                    >
                      Löschen
                    </button>
                  )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="muted small">Ein Anfragetyp, der in Anfragen verwendet wird, lässt sich nicht löschen.</p>
      </section>
    </>
  );
}
